import { ReactNode, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { Grid } from '@mui/material';

import { UserApi } from '@/api';
import { RenderFormField } from '@/components';
import { LocalStorageItems } from '@/constants';
import { useFormConfigLoader } from '@/hooks/useFormConfigLoader';
import {
  AgeGroup,
  ICFSMasterName,
  IKaseInvolvementForm,
  IKaseMasterName,
  getKaseMasterNameInvolvementItems,
  // getKaseMasterNameInvolvementLabel,
  IMasterNameOrganization,
  IMasterNamePerson,
  MasterNameInvolvement,
  // KaseMasterOrganizaionInvolvement,
  kaseOffenderForm,
  MasterNameBehaviors,
  MasterNameType,
  OffenderType,
} from '@/models';
import {
  getItemDynamicOptions,
  handleDefaultValueRules,
} from '@/pages/Kases/KaseCrimes/utilities';
import { handleReportClasses } from '@/pages/Kases/NIBRS/utils';
import { useAgencyStore, useToastStore } from '@/store';
import { formatPersonName } from '@/utils';

import {
  IFieldItem,
  OrganizationInvolvementKaseDisabled,
  OrganizationInvolvementKaseHidden,
  setInvolvementKaseFormFields,
} from './data';

interface InvolvementFormProps {
  isReadOnlyNames: boolean;
  involvementOptions: typeof MasterNameInvolvement;
  hasPredefinedInvolvement?: boolean;
  currentInvolvement?: MasterNameInvolvement | MasterNameInvolvement[];
  formFields: IFieldItem[];
  currentMastername?: (ICFSMasterName | IKaseMasterName) & {
    additionalInfo?: Record<string, any>;
  };
  entityId?: string;
  entityKind?: 'offender' | 'victim';
}

export const InvolvementForm = ({
  isReadOnlyNames,
  currentMastername,
  currentInvolvement,
  hasPredefinedInvolvement = false,
  formFields,
  entityId,
  entityKind,
}: InvolvementFormProps) => {
  const [conditionalFieldsTest, setConditionalFields] = useState({});
  const { resetField, watch, setValue, register } = useFormContext();
  const [searchParams] = useSearchParams();
  const formValues = watch();
  const { filteredOptions: offenseFilteredOptions } = useFormConfigLoader({
    formName: 'ARREST',
    optionsName: 'OFFENSE',
    category: 'INVOLVEMENT',
  });

  const { currentAgency } = useAgencyStore();
  const { updateToast } = useToastStore();

  const [employeeList, setEmployeeList] = useState<
    {
      value: any;
      label: string;
    }[]
  >([]);

  const [oriList, setOriList] = useState<
    {
      value: any;
      label: string;
    }[]
  >([]);

  useEffect(() => {
    fetchOfficers();
  }, [currentMastername?.masterName?.type]);

  useEffect(() => {
    setOriList(
      [
        ...(currentAgency?.oriNumber
          ? [
              {
                label: `${currentAgency?.oriNumber || ''} = ${
                  currentAgency?.name || ''
                }`,
                value: currentAgency?.oriNumber,
              },
            ]
          : []),
        ...(formValues?.officerOriJurisdiction
          ? [
              {
                label: formValues?.officerOriJurisdiction,
                value: formValues?.officerOriJurisdiction,
              },
            ]
          : []),
      ].filter(
        (item, index, fullArray) =>
          index ===
          fullArray.findIndex((existing) => existing.value === item.value),
      ),
    );
  }, [formValues?.victimType]);

  useEffect(() => {
    const subscription = watch((_value, { name }) => {
      if (name === 'victimType') {
        setValue('officerOriJurisdiction', undefined);
        setValue('officerActivity', undefined);
        setValue('officerAssignmentType', undefined);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, formValues?.victimType]);

  const { fetchAgency } = useAgencyStore();
  const agencyId = localStorage.getItem(LocalStorageItems.CurrentAgencyId);
  useEffect(() => {
    if (agencyId) {
      fetchAgency(agencyId);
    }
  }, [agencyId]);

  useEffect(() => {
    const subscription = watch((value) => {
      let updatedFields: Record<string, any> = {};
      if ((value as kaseOffenderForm)?.offenderType) {
        const offenderTypeValue = (value as kaseOffenderForm).offenderType;

        updatedFields = {
          ...updatedFields,
          checkOffenderType: offenderTypeValue === OffenderType.CHARGED,
        };

        updatedFields = {
          ...updatedFields,
        };
      }

      if (value?.masterNameType) {
        updatedFields = {
          ...updatedFields,
          isOrganization: value?.masterNameType !== 'ORGANIZATION',
        };
      }

      const personDobNotSet =
        currentMastername?.masterName?.type === MasterNameType.PERSON &&
        !!(currentMastername?.masterName as IMasterNamePerson)?.birthday ===
          false;

      // Handle ageGroup changes - clear age/ageRange when ageGroup is cleared
      if (!value?.ageGroup) {
        // Only clear if values exist to prevent infinite loop
        if (value?.age !== undefined) {
          setValue('age', undefined);
        }
        if (value?.ageRange !== undefined) {
          setValue('ageRange', undefined);
        }
        updatedFields = {
          ...updatedFields,
          checkExactAge: false,
          checkAgeRange: false,
          checkDobNotSet: personDobNotSet,
        };
      } else {
        updatedFields = {
          ...updatedFields,
          ...((value?.ageGroup === AgeGroup.UNKNOWN ||
            value?.ageGroup === AgeGroup.OVER_NINETY_EIGHT_YEARS_OLD) && {
            age: undefined,
          }),
          checkExactAge:
            personDobNotSet && value.ageGroup === AgeGroup.EXACT_AGE,
          checkAgeRange:
            personDobNotSet && value.ageGroup === AgeGroup.AGE_RANGE,
          checkDobNotSet: personDobNotSet,
        };
      }

      setConditionalFields((state) => ({
        ...state,
        ...updatedFields,
      }));
    });
    return () => subscription.unsubscribe();
  }, [watch, currentMastername]);

  useEffect(() => {
    const hasInvolvement = Array.isArray(formValues?.involvement)
      ? formValues.involvement.length > 0
      : !!formValues?.involvement;
    if (hasInvolvement || formValues.victimType) {
      handleDefaultValueRules<
        Omit<IKaseInvolvementForm, 'ageGroup' | 'residenceStatus'>
      >(offenseFilteredOptions, setValue, formValues, {
        getOriNumber: () => [currentAgency?.oriNumber],
      });
    }
  }, [formValues?.victimType, formValues?.involvement]);

  useEffect(() => {
    if (
      formValues.ageGroup === AgeGroup.AGE_RANGE &&
      !Array.isArray(formValues.age)
    ) {
      let ageValue = formValues.age?.toString().padStart(4, '0');
      ageValue = ageValue
        ? [Number(ageValue.slice(0, 2)), Number(ageValue.slice(2))]
        : [0, 20];
      setValue('age', undefined);
      setValue('ageRange', ageValue);
    }
    if (formValues.ageGroup === AgeGroup.EXACT_AGE) {
      setValue('ageRange', undefined);
      setValue(
        'age',
        Array.isArray(formValues.age)
          ? formValues.age[1]
          : formValues.age || '',
      );
    }
  }, [formValues.ageGroup]);

  const fetchOfficers = async () => {
    const filterParams = {
      'roles.role': {
        $in: ['RMS_CASES_FULL_ACCESS', 'RMS_CASES_VIEW'],
      },
    };
    try {
      const { data } = await UserApi.get({
        limit: 1000,
        filter: JSON.stringify(filterParams),
      });
      const officersMap = data.results.map((user) => ({
        value: user._id,
        label: formatPersonName(user),
      }));
      setEmployeeList([...officersMap]);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleInputChange = (val: string | string[], field: IFieldItem) => {
    const isAgeGroupChange = field.name.toLowerCase() === 'agegroup';
    if (!isAgeGroupChange) return;

    // User removes value in age group field
    if (isAgeGroupChange && !val) {
      resetField('ageGroup');
      resetField('age');
      resetField('ageRange');
    }
  };

  const getOptionItems: { [key: string]: any } = {
    getOriItems: oriList,
    getUsers: employeeList,
    getBehaviorItems: Object.entries(MasterNameBehaviors).map(
      ([value, label]) => ({ label, value }),
    ),
    getInvolvementItems: getKaseMasterNameInvolvementItems(),
  };

  const urlId = searchParams.get('id');
  const urlRole = searchParams.get('role');
  const isRoleMatch = urlRole && entityKind ? urlRole === entityKind : true;
  const scopedFieldKey =
    entityId !== undefined
      ? urlId === entityId && isRoleMatch
        ? searchParams.get('fieldKey')
        : null
      : searchParams.get('fieldKey');

  const renderRelatedFields = (): ReactNode => {
    const hasInvolvement = Array.isArray(currentInvolvement)
      ? currentInvolvement.length > 0
      : !!currentInvolvement;
    if (!hasInvolvement) {
      return;
    }
    const fields = formFields;
    return fields ? renderFormFields(fields) : <></>;
  };

  const renderFormFields = (fieldsArray: Array<IFieldItem>) => {
    return (
      <Grid flex={1} container columnSpacing={3} sx={{ mt: 2 }}>
        {fieldsArray.map((field) => {
          const { items, rules, setDisable, label } =
            getItemDynamicOptions(
              field.name,
              offenseFilteredOptions,
              formValues,
            ) || {};
          const { shouldDisplayCallback, ...fieldRest } = field;
          const shouldRender = shouldDisplayCallback
            ? conditionalFieldsTest[
                shouldDisplayCallback as keyof typeof conditionalFieldsTest
              ]
            : true;
          const dynamicRenderDisabled = setDisable && setDisable(formValues);
          let isDisabled = isReadOnlyNames;
          if (
            currentMastername?.masterName?.type === MasterNameType.ORGANIZATION
          ) {
            if (OrganizationInvolvementKaseHidden.includes(fieldRest.name)) {
              return null;
            }
            if (OrganizationInvolvementKaseDisabled.includes(fieldRest.name)) {
              isDisabled = true;
            }
          }
          const combinedRules = { ...rules, ...fieldRest.rules };
          return shouldRender && !dynamicRenderDisabled ? (
            fieldRest.type === 'hidden' ? (
              <input type="hidden" {...register(fieldRest.name)} />
            ) : (
              <Grid
                item
                key={fieldRest.name}
                xs={6}
                lg={4}
                sx={{
                  position: 'relative',
                }}
                className={handleReportClasses(fieldRest.name, scopedFieldKey)}
              >
                <RenderFormField
                  key={fieldRest.name}
                  {...fieldRest}
                  items={
                    items?.length
                      ? items
                      : typeof fieldRest.items === 'string' &&
                        fieldRest.items in getOptionItems
                      ? getOptionItems[fieldRest.items as string]
                      : fieldRest.items
                  }
                  label={fieldRest.label || label}
                  {...(fieldRest.type !== 'select' && {
                    onInputChange: (e: any) => handleInputChange(e, fieldRest),
                  })}
                  {...(combinedRules && { rules: combinedRules })}
                  {...(fieldRest.name === 'ageGroup' && {
                    clearable: true,
                    clearButtonVariant: 'icon',
                  })}
                  disabled={isDisabled}
                />
              </Grid>
            )
          ) : null;
        })}
      </Grid>
    );
  };

  return offenseFilteredOptions.length ? (
    <>
      {hasPredefinedInvolvement &&
        renderFormFields(setInvolvementKaseFormFields)}
      {renderRelatedFields()}
    </>
  ) : (
    <>loading</>
  );
};

export default InvolvementForm;

InvolvementForm.prepareData = (
  values: IKaseInvolvementForm,
  currentInvolvement:
    | MasterNameInvolvement
    | MasterNameInvolvement[]
    | undefined,
  fields: IFieldItem[],
  updateToast: (arg0: { message: string; open: boolean }) => void,
) => {
  const currentInvolvementValue = Array.isArray(currentInvolvement)
    ? currentInvolvement
    : currentInvolvement
    ? [currentInvolvement]
    : [];
  const involvementValue = Array.isArray(values.involvement)
    ? values.involvement
    : values.involvement
    ? [values.involvement]
    : [];
  if (!currentInvolvementValue.length || !involvementValue.length) return;
  // const fields = kasesInvolvementFields[currentInvolvement];
  if (!fields) {
    updateToast({ message: 'invalid involvement ', open: true });
    return;
  }
  const data = {
    relationship: {
      behavior: values.behavior,
      involvement: involvementValue,
    },
  } as IKaseMasterName;

  const additionalInfo = Object.values(fields).reduce((accumulated, item) => {
    let currentValue = values[item.name as keyof typeof values];

    if (currentValue === undefined || currentValue === '') {
      return {
        ...accumulated,
        [item.name]: null,
      };
    }
    if (item.name === 'age' || item.name === 'ageRange') {
      if (
        values.ageGroup === AgeGroup.UNKNOWN ||
        values.ageGroup === AgeGroup.OVER_NINETY_EIGHT_YEARS_OLD
      ) {
        return {
          ...accumulated,
          age: '',
          ageRange: '',
        };
      }
      if (item.name === 'ageRange' && values.ageGroup === AgeGroup.EXACT_AGE) {
        return {
          ...accumulated,
          ageRange: null,
        };
      }

      // if age group is age_range
      // but value isn't array, convert to array
      if (values.ageGroup === AgeGroup.AGE_RANGE) {
        currentValue = Array.isArray(currentValue)
          ? currentValue
          : [0, Number(currentValue)];
      }

      currentValue = Array.isArray(currentValue)
        ? currentValue
            .map((rangeItem) => rangeItem.toString().padStart(2, '0'))
            .join('')
        : currentValue?.toString().padStart(4, '0');

      return {
        ...accumulated,
        age: currentValue,
      };
    }

    return {
      ...accumulated,
      [item.name]: currentValue,
    };
  }, {} as Record<string, any>);
  // Always send wasArrested (boolean or null)
  if (typeof values.wasArrested === 'number') {
    additionalInfo.wasArrested = values.wasArrested;
  } else {
    additionalInfo.wasArrested = null;
  }

  // Always send ageGroup and age (value or null) to allow clearing
  if (values.ageGroup) {
    additionalInfo.ageGroup = values.ageGroup;
    if (!additionalInfo.age) {
      additionalInfo.age = null;
    }
  } else {
    additionalInfo.ageGroup = null;
    additionalInfo.age = null;
  }
  data.additionalInfo = additionalInfo;
  return data;
};

InvolvementForm.prepareReset = (res: IKaseMasterName) => {
  let ageValue = res.additionalInfo?.age;
  if (ageValue) {
    ageValue =
      res.additionalInfo?.ageGroup === AgeGroup.AGE_RANGE
        ? [Number(ageValue.slice(0, 2)), Number(ageValue.slice(2))]
        : +ageValue;
  }

  const involvementValue = Array.isArray(res?.relationship?.involvement)
    ? res.relationship.involvement
    : res?.relationship?.involvement
    ? [res.relationship.involvement]
    : undefined;
  let resetVals = {
    ...res.relationship,
    _id: res.masterName?._id,
    ...res.additionalInfo,
    masterNameType: res.masterName?.type.toString(),
    wasArrested: res.additionalInfo?.wasArrested,
    age: ageValue || undefined,
    ageRange: ageValue || undefined,
    ageGroup: res.additionalInfo?.ageGroup || undefined,
    interviewedBy: res.additionalInfo?.interviewedBy?._id || undefined,
    behavior: res.relationship?.behavior || MasterNameBehaviors['N/A'],
    involvement: involvementValue,
  };

  if (res.masterName?.type === MasterNameType.ORGANIZATION) {
    const organizationType = (
      res.masterName as IMasterNameOrganization
    ).organizationType
      ?.toString()
      .toLowerCase()
      .replaceAll('_', ' ');

    const victimType = organizationType?.toUpperCase() || '';
    resetVals = {
      ...resetVals,
      behavior: res.relationship?.behavior || MasterNameBehaviors['N/A'],
      // involvement: [KaseMasterOrganizaionInvolvement.VICTIM],
      age: resetVals?.age ? resetVals.age : undefined,
      ageRange: resetVals?.ageRange ? resetVals.ageRange : undefined,
      // residenceStatus, // TODO commented out by IVICA
      // levelOfInjury,
      // medicalTreatment,
      // ...(injuryType && { injuryType }),
      ...(organizationType && victimType && { victimType: victimType[0] }),
      masterNameType: res.masterName.type,
    };
  }
  return resetVals;
};
